import { rephrasePrompt } from "../../api/copilot/prompt.api";
import { Article, AttachFile, Download } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import ReplayIcon from "@mui/icons-material/Replay";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Avatar as MuiAvatar,
  Tooltip as ReactTooltip,
  Stack,
  Typography,
} from "@mui/material";
import { marked } from "marked";
import PublicIcon from "@mui/icons-material/Public";
import React, { useEffect, useMemo, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

import "./copilot.css";

import { downloadFile } from "../chatpage/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import ChatInput from "./chat-input";
import CopyButton from "./copy-button";
import DotLoader from "./dot-loader";
import EditButton from "./edit-button";
import FixPromptButton from "./fix-prompt-button";
import FeedbackPopup from "./feedback-popup";
import MarkdownRenderer from "./markdown-view";
import RegenerateButton from "./regenerate-button";

import PDFViewer from "@/components/chatpage/pdf-viewer-iframe";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import useCopilotStore from "@/store/copilotStore";
import { parseSources } from "@/utils/util";
import { FALLBACK_RESPONSE_EMPTY } from "@/constants/copilot";

import { CloseOutlined } from "@mui/icons-material";

const UserMessage = ({ message, userStore, onEdit, isLastUserMessage, stopMessageId, chatLoading, conversationId, uploadAttachment }: any) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState(message?.data?.content);
    const [editedAttachments, setEditedAttachments] = useState(message?.attachments?.additional_files || []);
    const [editedFileStates, setEditedFileStates] = useState([]);

    useEffect(() => {
        const newAttachments = editedFileStates
            .filter(fs => fs.doc_url)
            .map(fs => fs.doc_url);

        const currentAttachments = new Set(editedAttachments);
        newAttachments.forEach(url => currentAttachments.add(url));

        if (editedAttachments.length !== currentAttachments.size) {
            setEditedAttachments(Array.from(currentAttachments));
        }
    }, [editedFileStates]);
    const [isHovering, setIsHovering] = useState(false);
    const [originalContent, setOriginalContent] = useState("");
    const [showRevert, setShowRevert] = useState(false);
    const handleEdit = () => {
      setEditedContent(message?.data?.content);
      setEditedAttachments(message?.attachments?.additional_files || []);
      setShowRevert(false);
      setIsEditing(true);
    };

    const handleSave = () => {
      onEdit(message.id, editedContent, editedAttachments);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditedContent(message?.data?.content);
      setEditedAttachments(message?.attachments?.additional_files || []);
      setShowRevert(false);
      setIsEditing(false);
    };

    const handleRephraseInEdit = async () => {
      if (!editedContent) return;
      try {
        if (!showRevert) {
          setOriginalContent(editedContent);
        }
        const data = await rephrasePrompt(editedContent, conversationId);
        let newContent = editedContent;
        if (data.rephrased_queries && data.rephrased_queries.length > 1) {
          newContent = data.rephrased_queries[1];
        } else if (data.rephrased_queries && data.rephrased_queries.length > 0) {
          newContent = data.rephrased_queries[0];
        }

        if (editedContent !== newContent) {
          setEditedContent(newContent);
          setShowRevert(true);
        }
      } catch (error) {
        console.error("Error in handleRephraseInEdit:", error);
      }
    };
    
    const handleRemoveAttachment = (indexToRemove: number) => {
      setEditedAttachments(editedAttachments.filter((_: any, index: number) => index !== indexToRemove));
    };

    const handlePasteInEdit = (event: React.ClipboardEvent) => {
      event.stopPropagation(); 

      const items = event.clipboardData.items;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        const newFileStates = files.map(file => ({
            file,
            preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            progress: 0,
        }));
        setEditedFileStates((prev: any) => {
            const currentLength = prev.length;
            files.forEach((file, index) => {
                uploadAttachment(file, index + currentLength, setEditedFileStates);
            });
            return [...prev, ...newFileStates];
        });
      }
    };

    const fileInputRef = React.useRef(null);

    const handleFileSelect = (event: any) => {
        const files = Array.from(event.target.files);
        const newFileStates = files.map(file => ({
            file,
            preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            progress: 0,
        }));

        setEditedFileStates((prev: any) => {
            const currentLength = prev.length;
            files.forEach((file, index) => {
                uploadAttachment(file, index + currentLength, setEditedFileStates);
            });
            return [...prev, ...newFileStates];
        });
    };

    const handleRephrase = async () => {
      if (!message?.data?.content) return;
      try {
        const data = await rephrasePrompt(message.data.content, conversationId);
        console.log(data);
        if (data.rephrased_queries && data.rephrased_queries.length > 1) {
          setEditedContent(data.rephrased_queries[1]);
        } else if (data.rephrased_queries && data.rephrased_queries.length > 0) {
          setEditedContent(data.rephrased_queries[0]);
        } else {
          setEditedContent(message.data.content);
        }
        setIsEditing(true);
      } catch (error) {
        console.error("Error in handleRephrase:", error);
        if (error.response) {
          console.error("Error response:", error.response);
        }
      }
    };

    useEffect(() => {
      setEditedContent(message?.data?.content);
    }, [message?.data?.content]);

    useEffect(() => {
      console.log("editedContent changed:", editedContent);
    }, [editedContent]);

    return (
      <Card
        style={{
          backgroundColor: "#F6F6F6",
          alignSelf: "flex-end",
          minWidth: isEditing ? "100%" : "auto",
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            {isEditing ? (
              <Box sx={{ width: "100%" }} onPaste={handlePasteInEdit}>
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "0.25rem",
                    border: "0.0625rem solid #ccc",
                    minHeight: "7rem",
                    marginBottom: "0.5rem",
                  }}
                />
                <div className="text-sm text-muted-foreground mb-2">
                  Use{" "}
                  <span className="bg-muted px-1 rounded-sm">
                    Shift + Enter
                  </span>{" "}
                  for a new line.
                </div>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 1, mb: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <Button onClick={handleRephraseInEdit} size="small" variant="outlined">
                      Fix My Prompt
                    </Button>
                    {showRevert && (
                      <Button
                        onClick={() => {
                          setEditedContent(originalContent);
                          setShowRevert(false);
                        }}
                        size="small"
                        variant="outlined"
                      >
                        Use Original Query
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {editedAttachments.map((attachment: any, index: number) => (
                  <Box key={index} display="flex" alignItems="center" justifyContent="space-between">
                    <AttachmentPreview
                      attachment={attachment}
                      index={index}
                    />
                    <IconButton onClick={() => handleRemoveAttachment(index)}>
                      <CloseOutlined />
                    </IconButton>
                  </Box>
                ))}
                <Button
                    startIcon={<AttachFile />}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Attach File
                </Button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                    multiple
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCancel}
                    className={`text-xs p-2 ml-2`}
                    style={{ border: "1px solid #137D00" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleSave}
                    className={`text-xs p-2 ml-2`}
                    style={{ border: "1px solid #137D00" }}
                  >
                    Generate
                  </Button>
                </Stack>
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, position: "relative" }}>
                  <MarkdownRenderer
                    content={message?.data?.content}
                    maxHeight=""
                    prose={true}
                  />
                </Box>
                <Avatar>
                  <AvatarImage src={userStore?.user?.profileImage} />
                  <AvatarFallback className="bg-green-200">
                    <MuiAvatar className="bg-green-200">
                      {userStore?.user?.name?.at(0)}
                    </MuiAvatar>
                  </AvatarFallback>
                </Avatar>
              </>
            )}
          </Stack>
          {!isEditing && message?.attachments?.additional_files?.map(
            (attachment: any, index: number) => (
              <AttachmentPreview
                key={index}
                attachment={attachment}
                index={index}
              />
            )
          )}
          {message?.stoppedByUser && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <CancelIcon color="error" />
                </TooltipTrigger>
                <TooltipContent className="bg-black">
                  <p className="text-white">This message was stopped</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardContent>
        {!isEditing && isLastUserMessage && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
              <EditButton onClick={handleEdit} disabled={chatLoading} />
              <FixPromptButton onClick={handleRephrase} disabled={chatLoading || !conversationId} />
          </div>
        )}
      </Card>
    );
  };

const AttachmentPreview = ({ attachment, index }: any) => (
  <Sheet key={index}>
    <SheetTrigger>
      <Stack direction="row-reverse" spacing={2} mt={2}>
        <Button
          variant="outlined"
          startIcon={<AttachFile />}
          sx={{ textTransform: "none" }}
        >
          {attachment?.split("/").at(-1)}
        </Button>
      </Stack>
    </SheetTrigger>
    <SheetContent style={{ maxWidth: "50vw" }}>
      <SheetHeader>
        <SheetTitle>
          {attachment?.startsWith("http")
            ? "Web"
            : attachment?.split("/").at(-1)}
        </SheetTitle>
        <SheetDescription>
          {attachment?.startsWith("http") ? (
            <iframe
              title={`${index}`}
              src={attachment}
              width="100%"
              height="37.5rem"
            />
          ) : (
            <PDFViewer gcsPath={attachment} />
          )}
        </SheetDescription>
      </SheetHeader>
    </SheetContent>
  </Sheet>
);

const formatResponseTime = (ms: any) => {
  if (!ms) return "";
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return minutes ? `${minutes} min ${seconds} sec` : `${seconds} sec`;
};

const SystemMessage = ({
  message,
  performRegeneration,
  updateFeedback,
  conversationType,
  responseTime,
}: any) => {
  const processMarkdownContent = (content: any) => {
    if (!content) return "";

    // escaped newlines
    let processed = content.trim().replace(/\\n/g, "\n");

    // [0, 1, 2, 3]* issue handdling
    processed = processed.replace(/]\*/g, "]\n*");

    return processed;
  };

  const isError =
    message.isError ||
    ["retry_message", "error_message", "fallback"].includes(message.message_id);

  const borderColorClass = isError
    ? "border-l-4 border-l-red-500"
    : "border-l-4 border-l-brand-primary";

  return (
    <div
      className={`max-w-[70vw] overflow-auto rounded-lg shadow-md bg-card border border-border ${borderColorClass}`}
    >
      <div className="p-4">
        <div className="flex items-start space-x-4 m-2">
          <span className={isError ? "text-red-500" : "text-brand-primary"}>
            {isError ? <ErrorIcon /> : <AutoFixHighIcon />}
          </span>
          <div className="text-base">
            {message?.data?.content ? (
              <MarkdownRenderer
                content={processMarkdownContent(message.data.content)}
                maxHeight=""
                prose={true}
                highlightCitations={true}
              />
            ) : (
              <MarkdownRenderer
                content={FALLBACK_RESPONSE_EMPTY}
                maxHeight=""
                prose={true}
              />
            )}
          </div>
        </div>

        <SourcesList sources={message?.sources} />
        {responseTime !== 0 &&
          !["retry_message", "fallback"].includes(message?.message_id) && (
            <div
              style={{
                color: "gray",
                fontSize: "0.7rem",
                marginTop: "0.5rem",
                padding: "0 1rem",
              }}
            >
              Response Time: {formatResponseTime(responseTime)}
            </div>
          )}

        {isError && (
          <div className="bg-red-50 p-2 rounded-md mt-2 ml-4">
            <p className="text-sm text-red-600 font-medium">
              Error occurred while processing your request.
            </p>
          </div>
        )}

        <p className="text-sm italic text-muted-foreground mt-2 ml-4 mb-2">
          This is AI generated content that requires human intervention. Review
          for accuracy and relevance.
        </p>

        <MessageActions
          message={message}
          performRegeneration={performRegeneration}
          conversationType={conversationType}
        />
      </div>
    </div>
  );
};

const SourcesList = ({ sources }: any) => {
  if (!sources?.length) return null;

  return (
    <Box sx={{ margin: "0 0 0 1rem" }}>
      {sources.map((source: any, index: number) => (
        <Stack key={index} direction="row" spacing={2} mt={2}>
          <SourcePreview source={source} index={index} />
        </Stack>
      ))}
    </Box>
  );
};

const SourcePreview = ({ source, index }: any) => (
  <Sheet>
    <SheetTrigger>
      {source?.index != null && (
        <Typography variant="body2" component="span" sx={{ mr: 1 }}>
          {source?.index}.
        </Typography>
      )}

      <Button
        variant="outlined"
        startIcon={<Article />}
        sx={{ textTransform: "none" }}
      >
        {parseSources(source?.source, source?.domain)} {" "}
        {source?.pages?.at(0)?.length !== 0 &&
          source?.type === "internal" &&
          `(p. ${source?.pages?.toString()})`}
      </Button>
    </SheetTrigger>
    <SheetContent style={{ maxWidth: "50vw" }}>
      <SheetHeader>
        <SheetTitle>
          {source?.source?.startsWith("http")
            ? "Web"
            : source?.source?.split("/").at(-1)}
        </SheetTitle>
        <SheetDescription>
          {source?.source?.startsWith("http") && (
            <>
              <p className="font-semibold mb-2 text-foreground">
                If there is problem with opening in the preview, please try
                opening in new tab{'. '}
                <a
                  href={source?.source}
                  target="_blank"
                  className="font-bold underline text-brand-primary"
                >
                  click here
                </a>
              </p>
              <p className="font-semibold text-red-600">
                Disclaimer: Due to firewall and content restrictions the web
                page might not be visible in preview
              </p>
            </>
          )}
          {source?.source?.startsWith("http") ? (
            <iframe
              title={`${index}${source?.source}`}
              src={source?.source}
              width="100%"
              height="37.5rem"
            />
          ) : (
            <>
              <div className="font-semibold">
                {source?.pages && `page no. ${source?.pages?.toString()}`}
              </div>
              <PDFViewer gcsPath={source?.source} />
            </>
          )}
        </SheetDescription>
      </SheetHeader>
    </SheetContent>
  </Sheet>
);

const MessageActions = ({
  message,
  performRegeneration,
  conversationType,
}: any) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const messages = useCopilotStore((state) => state.messages);
  const isLastSystemMessage = useMemo(() => {
    if (!messages.length) return false;

    const systemMessages = messages.filter(
      (msg) => msg.message_type !== "User"
    );
    const lastSystemMessage = systemMessages[systemMessages.length - 1];

    return message.id === lastSystemMessage?.id;
  }, [messages, message]);

  const downloadContent = async () => {
    let htmlCode = marked(message.data.content);
    const fileName = "response";
    const fileType = "pdf";
    await downloadFile(htmlCode, fileType, fileName);
  };

  return (
    <div className="button-container">
      <CopyButton
        text={message?.data?.content}
        className="max-h-5 text-xs lg:z-10"
      />

      {isLastSystemMessage &&
        conversationType !== "compare" &&
        !["retry_message"].includes(message?.message_id) && (
          <RegenerateButton onClick={performRegeneration} />
        )}

      {conversationType === "compare" && (
        <ReactTooltip title="Download">
          <IconButton
            sx={{ margin: "0 0.5rem 0 0" }}
            className="lg:z-10"
            onClick={() => downloadContent()}
          >
            <Download />
          </IconButton>
        </ReactTooltip>
      )}

      {!["retry_message", "fallback"].includes(message?.message_id) && (
        <FeedbackPopup
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          responseId={message?.id ?? message?.message_id}
          gaFeedbackType="copilot"
        />
      )}
    </div>
  );
};

const ConversationView = ({
  copilotStore,
  userStore,
  chatLoading,
  performRegeneration,
  updateFeedback,
  userPrompt,
  handlePromptChange,
  setUserPrompt,
  fileStates,
  
  additionalPrompt,
  setAdditionalPrompt,
  selectedSource,
  setSelectedSource,
  stopChat,
  fileInputRef,
  handleFileSelect,
  setFileStates,
  messagesEndRef,
  fileError,
  setFileError,
  conversationType,
  responseTime,
  handleEditUserMessage,
  stopReason,
  systemPrompt,
  setSystemPrompt,
  handleSystemPromptUpdate,
  additionalPromptOpen,
  setAdditionalPromptOpen,
  conversationId,
  handleSendUserQuery,
  uploadAttachment,
}: any) => {
  const lastUserMessageId = useMemo(() => {
    const userMessages = copilotStore?.messages?.filter(
      (msg: any) => msg.message_type === "User"
    );
    return userMessages.length > 0
      ? userMessages[userMessages.length - 1].id
      : null;
  }, [copilotStore?.messages]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "2rem",
          minHeight: "70vh",
        }}
      >
        <Grid
          container
          sx={{
            display: "block",
            minHeight: "50vh",
            maxHeight: "65vh",
            overflowY: "scroll",
            padding: "0 3rem",
          }}
          spacing={4}
        >
          {copilotStore?.messages?.map((message: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: `${message?.message_type === "User" ? "flex-end" : "flex-start"}`,
                width: "100%",
                maxWidth: "Calc(100% - 5rem)",
                margin: "0.5rem 1rem 0.5rem 3rem",
              }}
            >
              {message?.message_type === "User" ? (
                <UserMessage
                  message={message}
                  userStore={userStore}
                  onEdit={handleEditUserMessage}
                  isLastUserMessage={message.id === lastUserMessageId}
                  stopMessageId={copilotStore?.stopMessageId || ""}
                  chatLoading={chatLoading}
                  conversationId={copilotStore.currentConversationId}
                  uploadAttachment={uploadAttachment}
                />
              ) : (
                <SystemMessage
                  message={message}
                  performRegeneration={performRegeneration}
                  updateFeedback={updateFeedback}
                  conversationType={conversationType}
                  responseTime={message?.response_time ?? responseTime}
                />
              )}
            </Box>
          ))}

          {copilotStore.chatLoading && (
            <Stack sx={{ margin: "2rem 0 0 2rem" }}>
              <DotLoader />
            </Stack>
          )}

          <div ref={messagesEndRef} />
        </Grid>
      </Box>
      <Grid
        item
        xs={12}
        md={9}
        display="flex"
        flexDirection="column"
        width="100%"
        justifyContent="center"
        alignItems="center"
        position="sticky"
        bottom="0"
        left="100"
        right="0"
        className="isolate"
      >
        <ChatInput
          userPrompt={userPrompt}
          handlePromptChange={handlePromptChange}
          setUserPrompt={setUserPrompt}
          fileStates={fileStates}
          handleSendUserQuery={handleSendUserQuery}
          additionalPrompt={additionalPrompt}
          setAdditionalPrompt={setAdditionalPrompt}
          selectedSource={selectedSource}
          setSelectedSource={setSelectedSource}
          chatLoading={chatLoading}
          stopChat={stopChat}
          fileInputRef={fileInputRef}
          handleFileSelect={handleFileSelect}
          setFileStates={setFileStates}
          fileError={fileError}
          setFileError={setFileError}
          systemPrompt={systemPrompt}
          setSystemPrompt={setSystemPrompt}
          handleSystemPromptUpdate={handleSystemPromptUpdate}
          additionalPromptOpen={additionalPromptOpen}
          setAdditionalPromptOpen={setAdditionalPromptOpen}
          conversationId={conversationId}
        />
        <div className="relative w-full px-2 py-2 text-center text-xs text-muted-foreground empty:hidden md:px-[3.75rem] bg-card mt-0.5 max-w-21">
          This is AI generated content that requires human intervention. Review
          for accuracy and relevance.
        </div>
      </Grid>
    </>
  );
};

export default ConversationView;
